import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { bulkEditApi } from '@/features/bulk-edit/api'
import { applyRules } from './definitions/fieldRules'
import fields from './definitions/formFields'
import {
  assignFieldType,
  type DropDownValue,
  type Field,
  type FieldAction,
  type FieldFromServer,
  type FieldName,
  type FieldValue,
  type Id,
  type MultiSelectValue,
  type MultiTextValue,
  type OptionRequirements,
  type SelectOption,
  type SelectOptionFromServer,
  type TextValue,
} from './types'

const initialValueByInputType: Record<string, FieldValue> = {
  textarea: '' as TextValue,
  textfield: '' as TextValue,
  multiselect: [] as MultiSelectValue,
  multitext: [] as MultiTextValue,
  dropdown: null as DropDownValue,
}

export type BulkEditState = {
  fields: Record<FieldName, Field>
  options: Record<FieldName, SelectOption[]>
  activeFields: Record<FieldName, boolean>
  openFields: Record<FieldName, boolean>
  fieldAction: Record<FieldName, FieldAction>
  values: Record<FieldName, FieldValue>
}

function getInitialState(): BulkEditState {
  const initialState = {
    fields: {} as Partial<Record<FieldName, Field>>,
    options: {} as Partial<Record<FieldName, SelectOption[]>>,
    activeFields: {} as Partial<Record<FieldName, boolean>>,
    openFields: {} as Partial<Record<FieldName, boolean>>,
    fieldAction: {} as Partial<Record<FieldName, FieldAction>>,
    values: {} as Partial<Record<FieldName, FieldValue>>,
  }

  // create initial values with field definitions

  Object.keys(fields).forEach((_fieldName) => {
    const fieldName = _fieldName as FieldName
    const field = assignFieldType(fields[fieldName] as Field)
    initialState.fields[fieldName] = field
    initialState.values[fieldName] = initialValueByInputType[field.inputType]
    initialState.openFields[fieldName] = false
    initialState.activeFields[fieldName] = false
    if (fields[_fieldName].appendable) {
      initialState.fieldAction[fieldName] = null
    }
  })

  return initialState as BulkEditState
}

const bulkEditSlice = createSlice({
  name: 'bulkEdit',
  initialState: getInitialState(),
  reducers: {
    setOpen: (state, { payload: fieldName }: PayloadAction<FieldName>) => {
      state.openFields[fieldName] = true
      applyRules(state, fieldName)
    },

    setClosed: (state, { payload: fieldName }: PayloadAction<FieldName>) => {
      state.openFields[fieldName] = false
      applyRules(state, fieldName)
    },

    setActive: (state, { payload: fieldName }: PayloadAction<FieldName>) => {
      state.activeFields[fieldName] = true
      state.openFields[fieldName] = true
      applyRules(state, fieldName)
    },

    setInactive: (state, { payload: fieldName }: PayloadAction<FieldName>) => {
      state.activeFields[fieldName] = false
      state.openFields[fieldName] = false
      applyRules(state, fieldName)
    },

    setValue: (
      state,
      action: PayloadAction<{ fieldName: FieldName; value: FieldValue }>,
    ) => {
      const { fieldName, value } = action.payload
      state.values[fieldName] = value

      bulkEditSlice.caseReducers.setActive(state, {
        type: 'setActive',
        payload: fieldName,
      })
    },

    setAction: (
      state,
      action: PayloadAction<{ fieldName: FieldName; fieldAction: FieldAction }>,
    ) => {
      const {
        payload: { fieldName, fieldAction },
      } = action

      state.fieldAction[fieldName] = fieldAction
      state.activeFields[fieldName] = true

      applyRules(state, fieldName)
    },

    setAllActions: (state, { payload: action }: PayloadAction<FieldAction>) => {
      for (const field of Object.keys(state.fieldAction)) {
        state.fieldAction[field] = action
        applyRules(state, field)
      }
    },
  },
  extraReducers: (builder) => {
    //when query to media options completes updates add options to state automatically.
    builder.addMatcher(
      bulkEditApi.endpoints.getBulkFields.matchFulfilled,
      (state, { payload }) => {
        payload.fields.forEach((field: FieldFromServer) => {
          const { fieldName, fieldOptions } = field

          const formattedOptions: SelectOption[] = fieldOptions
            .filter(isValidOption)
            .map((opt) => {
              const newReqs = normalizeRequirements(opt)
              return { ...opt, ...{ requires: newReqs } } as SelectOption
            })

          state.options[fieldName as FieldName] = formattedOptions
        })
        return state
      },
    )
  },
})

export const {
  setOpen,
  setClosed,
  setActive,
  setInactive,
  setValue,
  setAction,
  setAllActions,
} = bulkEditSlice.actions

export default bulkEditSlice.reducer

// Helpers

function isValidOption(o: SelectOptionFromServer) {
  return !(o.code === '')
}

/**
 * @description Reduce server options to optimized requirement object
 * @param option
 * @returns
 */
function normalizeRequirements(option: SelectOptionFromServer) {
  const reducedReqs: OptionRequirements = {}

  option.requires.forEach((reqField: FieldFromServer) => {
    const codes: Id[] = reqField.fieldOptions.map((o) => o.code)

    if (codes.length > 0) {
      reducedReqs[reqField.fieldName] = codes
    }
  })

  if (Object.keys(reducedReqs).length === 0) return null

  return reducedReqs
}
